import Fuse from 'fuse.js';

import { useNuxtApp } from '#app';
import type { Ref } from 'vue';
import type { Order } from '@/_api/models/order';
import type { FilterConfig, FilterState } from '@/_api/types/interfaces/filter';
import { OrderItemPaymentType, OrderItemPaymentTypeLabels } from '@/_api/models/orderItem';
import { EmployeeType } from '@/_api/models/employee';

import { useEmployeeStore } from '~/stores/useEmployeeStore';
import { useServiceStore } from '~/stores/useServiceStore';
import { useProductStore } from '~/stores/useProductStore';
import { useDiscountModelStore } from '~/stores/useDiscountModelStore';
import { useVoucherStore } from '~/stores/useVoucherStore';

import { useFilterController, type FilterControllerReturn } from '../useFilterController';

export interface OrderFilterReturn extends FilterControllerReturn<Order> {
  updateEmployeeOptions: () => void;
  updateServiceOptions: () => void;
  updateProductOptions: () => void;
  updateDiscountOptions: () => void;
  updateVoucherOptions: () => void;
}

export const useOrderFilters = (): OrderFilterReturn => {
  const { $api, $i18n } = useNuxtApp() as any;

  let states: Ref<Record<string, FilterState>>;

  const constructLabel = (value: any[]): string => {
    const labels = value.slice(0, 3).map(({ title }: any) => title).join($i18n.t('general.listSeparator'));
    if (value.length > 3) {
      return $i18n.t('general.andMore', { list: labels, count: value.length - 3 });
    }
    return labels;
  };

  const controller = useFilterController<Order>({
    service: $api.order,
    matchAll: true,
    customFields: { modelName: 'order' },
    filters: {
      search: {
        type: 'search',
        label: $i18n.t('general.search'),
        strategy: 'local',
        defaultValue: '',
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => value || null,
        getParams: (value: any) => ({ search: value }),
        matches: (orders: Order[], value: any) => {
          const fuse = new Fuse(orders, {
            keys: [
              'customer.firstName',
              'customer.lastName',
              'customer.company',
              'priceLabel',
              'orderNumber',
            ],
          });
          return fuse.search(value).map(({ item }) => item);
        },
      } as FilterConfig<Order>,

      dates: {
        type: 'date',
        label: $i18n.t('general.dates'),
        strategy: 'remote',
        component: 'OrderDateRange',
        force: true,
        defaultValue: { start: null, finish: null },
        isEmpty: (value: any) => !value.start && !value.finish,
        validate: (value: any) => {
          const { start, finish } = value;
          if (!start || !finish) {
            return { status: false, message: $i18n.t('order.filters.invalidDate') };
          }
          return null;
        },
        valueLabel: () => false as any,
        getParams: (value: any) => ({
          dateFrom: value.start,
          dateTo: value.finish,
        }),
      } as FilterConfig<Order>,

      paymentStatus: {
        type: 'paymentStatus',
        label: $i18n.t('order.filters.paymentStatus'),
        strategy: 'remote',
        component: 'SegmentField',
        options: [
          { label: $i18n.t('order.filters.paidInFull'), value: 'paid' },
          { label: $i18n.t('order.unpaid'), value: 'unpaid' },
          { label: $i18n.t('order.partial'), value: 'partial' },
        ],
        props: {
          outline: true,
          baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
        },
        defaultValue: null,
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => {
          switch (value) {
            case 'paid': return $i18n.t('order.filters.paidInFull');
            case 'unpaid': return $i18n.t('order.unpaid');
            case 'partial': return $i18n.t('order.partial');
            default: return $i18n.t('general.all');
          }
        },
        getParams: (value: any) => ({ paymentStatus: value }),
      } as FilterConfig<Order>,

      saleStatus: {
        type: 'saleStatus',
        label: $i18n.t('order.saleStatus'),
        strategy: 'remote',
        component: 'SegmentField',
        options: [
          { label: $i18n.t('general.open'), value: 'open' },
          { label: $i18n.t('general.closed'), value: 'closed' },
        ],
        props: {
          outline: true,
          baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
        },
        defaultValue: null,
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => {
          return value === 'open' ? $i18n.t('general.open') : $i18n.t('general.closed');
        },
        getParams: (value: any) => ({ status: value }),
      } as FilterConfig<Order>,

      employees: {
        type: 'employees',
        label: $i18n.t('general.employees'),
        info: $i18n.t('order.filters.employees'),
        strategy: 'remote',
        component: 'USelectMenu',
        props: {
          multiple: true,
          by: 'idEmployee',
          placeholder: $i18n.t('general.employees'),
          labelKey: 'displayName',
        },
        defaultValue: [],
        options: [],
        isEmpty: (value: any) => !value?.length,
        valueLabel: (value: any) => {
          if (value?.length) {
            const labels = value
              .slice(0, 3)
              .map((e: any) => e.displayName)
              .join($i18n.t('general.listSeparator'));
            if (value.length > 3) {
              return $i18n.t('general.andMore', { list: labels, count: value.length - 3 });
            }
            return labels;
          }
          return $i18n.t('employee.all');
        },
        getParams: (value: any) => ({
          employees: value.map(({ idEmployee }: any) => idEmployee),
        }),
      } as FilterConfig<Order>,

      services: {
        type: 'services',
        label: $i18n.t('general.services'),
        info: $i18n.t('order.filters.services'),
        strategy: 'remote',
        component: 'USelectMenu',
        props: {
          multiple: true,
          by: 'idService',
          placeholder: $i18n.t('general.services'),
          labelKey: 'title',
          filterFields: ['title'],
        },
        defaultValue: [],
        options: [],
        isEmpty: (value: any) => !value?.length,
        valueLabel: (value: any) => {
          if (value?.length) return constructLabel(value);
          return $i18n.t('order.filters.allServices');
        },
        getParams: (value: any) => ({
          services: value.map(({ idService }: any) => idService),
        }),
      } as FilterConfig<Order>,

      products: {
        type: 'products',
        label: $i18n.t('general.products'),
        info: $i18n.t('order.filters.products'),
        strategy: 'remote',
        component: 'USelectMenu',
        props: {
          multiple: true,
          by: 'idProduct',
          placeholder: $i18n.t('general.products'),
          labelKey: 'name',
          filterFields: ['name'],
        },
        defaultValue: [],
        options: [],
        isEmpty: (value: any) => !value?.length,
        valueLabel: (value: any) => {
          if (value?.length) {
            const labels = value
              .slice(0, 3)
              .map((p: any) => p.name || p.title || '-')
              .join($i18n.t('general.listSeparator'));
            if (value.length > 3) {
              return $i18n.t('general.andMore', { list: labels, count: value.length - 3 });
            }
            return labels;
          }
          return $i18n.t('product.all');
        },
        getParams: (value: any) => ({
          products: value.map(({ idProduct }: any) => idProduct),
        }),
      } as FilterConfig<Order>,

      discounts: {
        type: 'discount',
        label: $i18n.t('general.discount'),
        strategy: 'remote',
        component: 'SelectField',
        options: [],
        defaultValue: null,
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => value?.title || null,
        getParams: (value: any) => ({
          discount: value?.idDiscountModel,
        }),
      } as FilterConfig<Order>,

      vouchers: {
        type: 'voucher',
        label: $i18n.t('general.promoCode'),
        strategy: 'remote',
        component: 'SelectField',
        features: ['vouchers'],
        options: [],
        defaultValue: null,
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => value ? `${value.name} (${value.code})` : null,
        getParams: (value: any) => ({
          voucher: value?.idVoucher,
        }),
      } as FilterConfig<Order>,

      paymentType: {
        type: 'paymentType',
        label: $i18n.t('order.filters.paymentType'),
        strategy: 'remote',
        component: 'SelectField',
        options: Object.values(OrderItemPaymentType)
          .filter((v) => typeof v === 'number')
          .map((value) => ({
            label: $i18n.t(OrderItemPaymentTypeLabels[value as OrderItemPaymentType]),
            value: { value, description: OrderItemPaymentTypeLabels[value as OrderItemPaymentType] },
          })),
        props: {
          outline: true,
        },
        defaultValue: null,
        isEmpty: (value: any) => !value,
        valueLabel: (value: any) => value?.description ? $i18n.t(value.description) : null,
        getParams: (value: any) => ({
          paymentType: value?.value,
        }),
      } as FilterConfig<Order>,
    },
    categories: [
      { label: $i18n.t('general.payments'), filters: ['paymentStatus'] },
      { label: $i18n.t('order.saleStatus'), filters: ['saleStatus'] },
      { label: $i18n.t('general.employees'), filters: ['employees'] },
      { label: $i18n.t('general.services'), filters: ['services'] },
      { label: $i18n.t('general.products'), filters: ['products'] },
      { label: $i18n.t('general.customFields'), filters: [], features: ['custom-fields'] },
    ],
    filterOptions: [
      {
        map() {
          return Object.values(useEmployeeStore().all)
            .filter(({ type }: any) => type === EmployeeType.Employee);
        },
        set(options: any[]) {
          if (states.value.employees) {
            states.value.employees.options = options;
          }
        },
      },
      {
        map() {
          const rows: any[] = [];
          for (const category of Object.values(useServiceStore().categorised) as any[]) {
            const services = category.services ?? [];
            if (!services.length) {
              continue;
            }
            const catLabel = category.name || category.title || '';
            rows.push({ type: 'label', title: catLabel });
            rows.push(...services);
          }
          return rows;
        },
        set(options: any[]) {
          if (states.value.services) {
            states.value.services.options = options;
          }
        },
      },
      {
        map() {
          const products = useProductStore().all;
          const grouped: Record<string, { label: string; products: any[] }> = {};

          const appendProduct = (product: any, fallbackCategory?: any) => {
            if (!product) {
              return;
            }

            const category = product.category || product.productCategory || fallbackCategory || null;
            const key = category?.idProductCategory?.toString?.() || category?.name || 'other';

            if (!grouped[key]) {
              grouped[key] = {
                label: category?.name || $i18n.t('general.other'),
                products: [],
              };
            }

            grouped[key].products.push(product);
          };

          if (products) {
            Object.values(products).forEach((entry: any) => {
              if (Array.isArray(entry?.products)) {
                entry.products.forEach((product: any) => appendProduct(product, entry.category || entry.productCategory));
                return;
              }

              appendProduct(entry);
            });
          }

          const rows: any[] = [];
          for (const group of Object.values(grouped).filter((g) => g.products.length > 0)) {
            rows.push({ type: 'label', name: group.label });
            rows.push(...group.products);
          }
          return rows;
        },
        set(options: any[]) {
          if (states.value.products) {
            states.value.products.options = options;
          }
        },
      },
      {
        map() {
          return Object.values(useDiscountModelStore().all).map((discountModel: any) => ({
            value: discountModel,
            label: discountModel.title,
          }));
        },
        set(options: any[]) {
          if (states.value.discounts) {
            states.value.discounts.options = options;
          }
        },
      },
      {
        map() {
          return Object.values(useVoucherStore().all).map((voucher: any) => ({
            value: voucher,
            label: `${voucher.name} (${voucher.code})`,
          }));
        },
        set(options: any[]) {
          if (states.value.vouchers) {
            states.value.vouchers.options = options;
          }
        },
      },
    ],
  });

  states = controller.filterStates;

  const updateEmployeeOptions = () => { controller.updateOptions(0); };
  const updateServiceOptions = () => { controller.updateOptions(1); };
  const updateProductOptions = () => { controller.updateOptions(2); };
  const updateDiscountOptions = () => { controller.updateOptions(3); };
  const updateVoucherOptions = () => { controller.updateOptions(4); };

  return {
    ...controller,
    updateEmployeeOptions,
    updateServiceOptions,
    updateProductOptions,
    updateDiscountOptions,
    updateVoucherOptions,
  };
};
